import React, { useState } from 'react';
import { faPen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from 'react-query';

import { trackEvent } from 'jf/analytics/Analytics';
import { RosterClient, SlackTargetingClient, SlackUser } from 'jf/api';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExCard } from 'jf/components/DevExCard';
import { DevExLink } from 'jf/components/DevExLink';
import { DevExText } from 'jf/components/DevExText';
import { useStudySlackTargetUpdater } from 'jf/pages/studyBuilder/targetRespondents/useStudySlackTargetUpdater';
import { useClientFlags } from 'jf/utils/useClientFlags';
import { useClientMutation, useClientQuery } from 'jf/utils/useClientQuery';

import { SlackTargetSearch } from './SlackTargetSearch';
import { SlackTargetTagList } from './SlackTargetTagList';

export const ManageRespondentsCard: React.FC<{
  studyRef: string;
}> = (props) => {
  const flags = useClientFlags();
  const studySlackTargetUpdater = useStudySlackTargetUpdater(props.studyRef);
  const queryClient = useQueryClient();

  const [isEditing, setIsEditing] = React.useState(false);
  const [isSending, setIsSending] = useState(false);

  const [unsavedSlackTargets, setUnsavedSlackTargets] = useState<SlackUser[]>([]);
  const [showRespondents, setShowRespondents] = useState(false);

  const { data: slackTargets } = useClientQuery(
    SlackTargetingClient.getStudySlackTargets,
    { studyRef: props.studyRef },
    { enabled: flags.slackTargeting }
  );
  const { mutateAsync: sendStudyMessages } = useClientMutation(
    SlackTargetingClient.sendStudyMessages
  );

  const numRespondents = slackTargets?.length || 0;

  const { data: targetedSlackRespondents, refetch } = useClientQuery(
    RosterClient.getRosterTargets,
    {
      studyRef: props.studyRef,
      slackSelected: true,
    }
  );

  const slackUsers = targetedSlackRespondents?.map((respondent) => respondent.slack!) || [];

  const onSend = async () => {
    setIsSending(true);
    const targetsToSave = [...slackUsers, ...unsavedSlackTargets];
    if (unsavedSlackTargets.length) {
      await studySlackTargetUpdater.update(targetsToSave);
    }

    await sendStudyMessages({ studyRef: props.studyRef!, inviteOnly: true });
    setUnsavedSlackTargets([]);
    refetch();
    queryClient.invalidateQueries(['GET_STUDY', props.studyRef]);

    trackEvent(`survey-analyzer:manage-slack-targets:send`);

    setIsSending(false);
    setIsEditing(false);
  };

  const slackTargetingEnabled = !!slackTargets?.length;

  return (
    <DevExCard
      title={
        <DevExText fontSize="md" strong>
          Survey Distribution
        </DevExText>
      }
      extra={
        isEditing ? (
          <>
            <DevExButton
              onClick={() => {
                setIsEditing(false);
                setUnsavedSlackTargets([]);
              }}
            >
              Cancel
            </DevExButton>
            <DevExButton
              type="primary"
              disabled={isSending || !unsavedSlackTargets.length}
              onClick={() => onSend()}
            >
              Send to {unsavedSlackTargets.length} new respondent
              {unsavedSlackTargets.length === 1 ? '' : 's'}
            </DevExButton>
          </>
        ) : (
          <DevExButton
            icon={<FontAwesomeIcon icon={faPen} />}
            type="outline"
            onClick={() => setIsEditing(true)}
          />
        )
      }
    >
      {!slackTargetingEnabled ? (
        <DevExText>The survey was sent via link.</DevExText>
      ) : (
        <>
          <DevExText lined>
            The survey was sent individually to{' '}
            <DevExText strong>{numRespondents} people </DevExText>
            in Slack.
          </DevExText>
          <DevExLink onClick={() => setShowRespondents(!showRespondents)}>
            {showRespondents ? 'Hide' : 'Show'} {numRespondents} respondent
            {numRespondents === 1 ? '' : 's'}
          </DevExLink>
          {isEditing && (
            <SlackTargetSearch
              studyRef={props.studyRef}
              unsavedSlackTargets={unsavedSlackTargets}
              onChange={setUnsavedSlackTargets}
            />
          )}

          {showRespondents && (
            <SlackTargetTagList
              studyRef={props.studyRef}
              unsavedSlackTargets={unsavedSlackTargets}
              onChange={setUnsavedSlackTargets}
            />
          )}
        </>
      )}
    </DevExCard>
  );
};
